/**
 * nativeNotificationService.js
 *
 * FCM token alır ve backend push_devices tablosuna kaydeder.
 * Tek kayıt noktası backend push_devices tablosudur.
 */

import { Capacitor } from "@capacitor/core";
import { apiRequest } from "./apiClient";
import { currentTimeZonePayload } from "../hooks/useUserTimezone";
import { getRuntimeVersionLabel } from "./runtimeAppInfo";

function isNative() {
  return Capacitor.isNativePlatform();
}

export function normalizeNativePushPermission(receive) {
  if (receive === "granted") return "granted";
  if (receive === "prompt" || receive === "prompt-with-rationale") return "prompt";
  return "denied";
}

export async function getNativePushPermissionStatus() {
  if (!isNative()) return "unsupported";
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const result = await FirebaseMessaging.checkPermissions();
  return normalizeNativePushPermission(result.receive);
}

export async function requestNativePushPermission() {
  if (!isNative()) return "unsupported";
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  const current = await FirebaseMessaging.checkPermissions();
  if (normalizeNativePushPermission(current.receive) !== "prompt") {
    return normalizeNativePushPermission(current.receive);
  }
  const result = await FirebaseMessaging.requestPermissions();
  return normalizeNativePushPermission(result.receive);
}

export async function openNativeNotificationSettings() {
  if (!isNative()) return false;
  if (Capacitor.getPlatform() === "android") {
    const plugin = Capacitor.Plugins?.AileCall;
    if (!plugin?.openNotificationSettings) return false;
    await plugin.openNotificationSettings();
    return true;
  }
  if (Capacitor.getPlatform() === "ios") {
    window.webkit?.messageHandlers?.aileNativeBridge?.postMessage({ action: "openNotificationSettings" });
    return true;
  }
  return false;
}

function getOrCreateNativeDeviceId() {
  const key = "familyPlannerNativeDeviceId";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID?.() || `native_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

function getAppLocale() {
  try { return localStorage.getItem("appLanguage") === "en" ? "en" : "tr"; }
  catch { return "tr"; }
}

/**
 * Native push iznini ister, FCM token alır, backend'e kaydeder.
 * lang parametresi verilirse (örn. App.js'teki useLanguage().lang) onu kullanır,
 * verilmezse localStorage'daki appLanguage'i okur. İkisi de aynı kaynaktan
 * geldiği için normalde eşittir; lang parametresi React state değişimini
 * doğrudan yakalamak için tercih edilir.
 */
export async function saveNativePushToken({ user, familyId, token, lang = null }) {
  if (!isNative() || (!user?.uid && !user?.firebase_uid) || !token) return null;
  const uid = user.firebase_uid || user.uid;
  const platform = Capacitor.getPlatform();
  const deviceId = `${platform}_${getOrCreateNativeDeviceId()}`;
  const locale = lang === "en" ? "en" : (lang === "tr" ? "tr" : getAppLocale());
  const appVersion = await getRuntimeVersionLabel();

  await apiRequest("/notifications/push-device", {
    method: "POST",
    body: JSON.stringify({
      userUid: uid,
      familyId: familyId || null,
      token,
      pushType: "fcm",
      platform,
      deviceId,
      locale,
      appVersion,
      ...currentTimeZonePayload(),
      timezoneSource: "push_registration",
    }),
  });
  return token;
}

export async function registerNativePush({ user, familyId, silent = false, lang = null }) {
  if (!isNative()) return null;
  if (!user?.uid && !user?.firebase_uid) return null;

  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

    let permStatus = await FirebaseMessaging.checkPermissions();
    if (normalizeNativePushPermission(permStatus.receive) === "denied") return null;
    if (normalizeNativePushPermission(permStatus.receive) === "prompt") {
      if (silent) return null;
      permStatus = await FirebaseMessaging.requestPermissions();
    }
    if (permStatus.receive !== "granted") return null;

    const { token: fcmToken } = await FirebaseMessaging.getToken();
    if (!fcmToken) { console.warn("[nativeNotif] FCM token alınamadı"); return null; }

    try {
      await saveNativePushToken({ user, familyId, token: fcmToken, lang });
    } catch (backendErr) {
      if (!silent) console.warn("[nativeNotif] Backend kayıt hatası:", backendErr?.message);
      return null;
    }

    // Tek kaynak backend push_devices tablosu.

    return fcmToken;
  } catch (err) {
    if (!silent) console.warn("[nativeNotif] Hata:", err?.message || err);
    return null;
  }
}
